// Race state machine: phase timing, camera follow, off-screen elimination
// and round scoring. Physics itself (stepCar) lives in car.ts; this module
// only tracks progress along the track, decides who is visible, who is
// eliminated, and when a round or a race ends.
//
// Task 8's stepRace is expected to call these once per tick, in this order,
// driven by advancePhase:
//
//   countdown:  advancePhase ticks phaseTime toward countdownLength. Cars
//               stay frozen simply because stepRace does not call stepCar
//               while phase is 'countdown'.
//   racing:     stepRace calls stepCar for every car first (car.ts), then
//               calls advancePhase, which internally runs, in order:
//                 updateProgress(track, car, dt)          for every car
//                 updateCamera(track, state, cfg, dt)
//                 updateElimination(track, state, cfg, dt)  (may move the
//                                                             phase to
//                                                             'roundEnd')
//                 checkFinished(state, cfg)                (lap-limit check,
//                                                            every tick)
//   roundEnd:   advancePhase ticks phaseTime toward cfg.roundEndPause; once
//               it expires:
//                 applyRoundScores(state, cfg)
//                 respawnAll(track, state)
//                 checkFinished(state, cfg)                (point-cap check)
//                 phase -> 'countdown' with countdownLength 1.5, unless
//                 checkFinished already moved it to 'finished'
//   finished:   terminal. advancePhase is a no-op.

import type { Vec2 } from '../math/vec';
import { v } from '../math/vec';
import type { CarState, RacePhase, Scores } from './types';
import type { PreparedTrack } from './track';
import { progressAlong, pointAtProgress, surfaceAt, collideWalls, resolveWallHit, inFalloff } from './track';
import { SPORTS_CAR } from './tuning';

export interface RaceConfig {
  pointCap: number;
  lapLimit: number;
  viewW: number; // camera view width, world metres, at fixed zoom
  viewH: number; // camera view height, world metres, at fixed zoom
  offscreenGrace: number; // seconds a car may spend off-screen before elimination
  roundEndPause: number; // seconds the roundEnd phase lasts
  countdown: number; // seconds of the initial countdown
}

export const DEFAULT_RACE_CONFIG: RaceConfig = {
  pointCap: 8,
  lapLimit: 3,
  viewW: 30,
  viewH: 18,
  offscreenGrace: 0.5,
  roundEndPause: 1.0,
  countdown: 3.0,
};

export interface CarSlot {
  id: number;
  name: string;
  colour: number;
  kind: 'human' | 'ai';
  difficulty?: 'easy' | 'normal' | 'hard';
}

export interface RaceState {
  tick: number;
  phase: RacePhase;
  phaseTime: number;
  cars: CarState[];
  slots: CarSlot[];
  scores: Scores;
  camera: { pos: Vec2; progress: number };
  leaderId: number;
  offscreen: Record<number, number>; // seconds each car has been off-screen
  roundWinnerId: number | null;
  ranking: number[];
  winnerId: number | null;
  seed: number;
  // Length of the current countdown phase: cfg.countdown for the initial
  // grid start, 1.5s for every countdown that follows a round end.
  countdownLength: number;
}

// How far a car's progress must be within the start/finish line for a
// centreline crossing to count as a lap wrap, as a fraction of track length.
const WRAP_LOW_BAND = 0.1;
const WRAP_HIGH_BAND = 0.9;

// Distance a car must have travelled backward, accumulated across ticks,
// before it counts as going the wrong way. Carried between ticks in
// car.backDist, plain serialisable state on CarState itself.
const WRONG_WAY_DISTANCE = 0.5;
const WRONG_WAY_SPEED = 2;

// Metres behind the leader (along the track) each respawned car is placed,
// per column-pair; alternated left/right by this lateral offset. Followers
// are placed two per row (a two-column grid) so an n-car field stays within
// RESPAWN_STEP * ceil((n-1)/2) metres of the leader instead of RESPAWN_STEP
// * (n-1) for a single file.
const RESPAWN_STEP = 2.2;
const RESPAWN_LATERAL = 1.2;

// How fast the camera closes the gap to the leader's progress, per second.
const CAMERA_EASE_RATE = 4;

export function createRace(track: PreparedTrack, slots: CarSlot[], cfg: RaceConfig, seed: number): RaceState {
  const cars: CarState[] = slots.map((slot, i) => {
    const grid = track.startGrid[i % track.startGrid.length]!;
    const pos: Vec2 = { ...grid.pos };
    const { progress: lapProgress } = progressAlong(track, pos);
    return {
      id: slot.id,
      pos,
      heading: grid.heading,
      vel: { x: 0, y: 0 },
      angVel: 0,
      airborne: 0,
      progress: lapProgress,
      lap: 0,
      lapProgress,
      wrongWayTime: 0,
      backDist: 0,
      eliminated: false,
      surface: surfaceAt(track, pos).name,
    };
  });

  const scores: Scores = {};
  const offscreen: Record<number, number> = {};
  for (const slot of slots) {
    scores[slot.id] = 0;
    offscreen[slot.id] = 0;
  }

  const first = cars[0];

  return {
    tick: 0,
    phase: 'countdown',
    phaseTime: 0,
    cars,
    slots,
    scores,
    camera: {
      pos: first ? { ...first.pos } : { x: 0, y: 0 },
      progress: first ? first.progress : 0,
    },
    leaderId: first ? first.id : -1,
    offscreen,
    roundWinnerId: null,
    ranking: cars.map((c) => c.id),
    winnerId: null,
    seed,
    countdownLength: cfg.countdown,
  };
}

// Finds the centreline segment index whose span contains arc-length s, the
// same search pointAtProgress does internally. Used to build a hint for
// progressAlong so a car's progress is tracked from where it was last tick
// instead of re-searching the whole loop every tick.
function segmentIndexAt(track: PreparedTrack, s: number): number {
  const n = track.centreline.length;
  const length = track.length;
  let sMod = length === 0 ? 0 : s % length;
  if (sMod < 0) sMod += length;
  for (let i = 0; i < n; i++) {
    const start = track.cumDist[i] ?? 0;
    const end = i + 1 < n ? track.cumDist[i + 1]! : length;
    if (sMod >= start && sMod < end) return i;
  }
  return n - 1;
}

// The only place that touches car.progress, car.lap, car.lapProgress and
// car.wrongWayTime. progress is the total distance travelled: lap * track
// length + lapProgress, where lapProgress comes from progressAlong using the
// previous segment as a search hint.
export function updateProgress(track: PreparedTrack, car: CarState, dt: number): void {
  const prevLapProgress = car.lapProgress;
  const prevTotal = car.progress;

  const hint = segmentIndexAt(track, prevLapProgress);
  const { progress: newLapProgress } = progressAlong(track, car.pos, hint);

  const length = track.length;
  const lowBand = length * WRAP_LOW_BAND;
  const highBand = length * WRAP_HIGH_BAND;

  let lap = car.lap;
  if (prevLapProgress > highBand && newLapProgress < lowBand) {
    lap += 1; // forward wrap over the start/finish line
  } else if (prevLapProgress < lowBand && newLapProgress > highBand) {
    lap -= 1; // backward wrap: reversing over the line does not gain a lap
  }

  const newTotal = lap * length + newLapProgress;
  const delta = newTotal - prevTotal;
  const speed = v.len(car.vel);

  if (delta < 0 && speed > WRONG_WAY_SPEED) {
    car.backDist -= delta;
    if (car.backDist > WRONG_WAY_DISTANCE) {
      car.wrongWayTime += dt;
    }
  } else {
    car.backDist = 0;
    car.wrongWayTime = 0;
  }

  car.lap = lap;
  car.lapProgress = newLapProgress;
  car.progress = newTotal;
}

// Highest-progress car among non-eliminated cars. Only when every car is
// eliminated does it fall back to considering all cars.
export function leaderOf(state: RaceState): number {
  const alive = state.cars.filter((c) => !c.eliminated);
  const pool = alive.length > 0 ? alive : state.cars;
  let best = pool[0]!;
  for (const car of pool) {
    if (car.progress > best.progress) best = car;
  }
  return best.id;
}

// Eases camera.progress toward the leader's progress and follows the
// centreline via pointAtProgress. Also keeps state.leaderId in sync every
// tick it runs, since the camera needs the leader anyway.
export function updateCamera(track: PreparedTrack, state: RaceState, cfg: RaceConfig, dt: number): void {
  const leaderId = leaderOf(state);
  state.leaderId = leaderId;
  const leader = state.cars.find((c) => c.id === leaderId);
  if (!leader) return;

  const t = Math.min(1, CAMERA_EASE_RATE * dt);
  state.camera.progress += (leader.progress - state.camera.progress) * t;
  state.camera.pos = pointAtProgress(track, state.camera.progress).pos;
}

// True if p sits inside the camera's viewW x viewH rect, centred on cam.
// Parametrised on a bare camera position (rather than a whole RaceState) so
// non-authoritative callers that only ever see the camera position over the
// wire - the client, from Snapshot.cam - can apply the exact same
// visibility rule the server uses for elimination, without needing a full
// RaceState of their own.
export function isVisibleAt(cam: Vec2, cfg: RaceConfig, p: Vec2): boolean {
  const halfW = cfg.viewW / 2;
  const halfH = cfg.viewH / 2;
  return Math.abs(p.x - cam.x) <= halfW && Math.abs(p.y - cam.y) <= halfH;
}

// True if p sits inside the camera's viewW x viewH rect, centred on
// camera.pos.
export function isVisible(state: RaceState, cfg: RaceConfig, p: Vec2): boolean {
  return isVisibleAt(state.camera.pos, cfg, p);
}

// Advances off-screen timers, marks cars eliminated (off-screen past grace,
// or in a falloff region), and decides whether the round should end: either
// all cars but one are eliminated, or the leader has pulled more than
// viewW * 0.45 of progress ahead of every other non-eliminated car. If the
// round ends because every car became eliminated on the same tick, the
// round winner is still the car with the highest progress (leaderOf falls
// back to all cars in that case).
export function updateElimination(track: PreparedTrack, state: RaceState, cfg: RaceConfig, dt: number): void {
  for (const car of state.cars) {
    if (car.eliminated) continue;

    if (inFalloff(track, car.pos)) {
      car.eliminated = true;
      continue;
    }

    if (isVisible(state, cfg, car.pos)) {
      state.offscreen[car.id] = 0;
    } else {
      const time = (state.offscreen[car.id] ?? 0) + dt;
      state.offscreen[car.id] = time;
      if (time > cfg.offscreenGrace) {
        car.eliminated = true;
      }
    }
  }

  const alive = state.cars.filter((c) => !c.eliminated);
  let shouldEnd = alive.length <= 1;

  if (!shouldEnd) {
    const leaderId = leaderOf(state);
    const leader = state.cars.find((c) => c.id === leaderId)!;
    const threshold = cfg.viewW * 0.45;
    shouldEnd = alive.every((c) => c.id === leaderId || leader.progress - c.progress > threshold);
  }

  if (shouldEnd) {
    state.roundWinnerId = leaderOf(state);
    state.phase = 'roundEnd';
    state.phaseTime = 0;
  }
}

// Places every car for the next round: the leader stays at its current
// progress but is put back on the centreline; the rest are ordered by
// progress descending (reverse order of progress along the track) and
// placed behind it in a two-column grid, RESPAWN_LATERAL either side of the
// centreline, RESPAWN_STEP metres back per row of two. This keeps an n-car
// field within RESPAWN_STEP * ceil((n-1)/2) metres of the leader instead of
// RESPAWN_STEP * (n-1) for a single file. Velocity, angVel and airborne are
// reset, elimination and off-screen timers cleared. Afterward every car is
// pushed clear of any wall it now overlaps.
export function respawnAll(track: PreparedTrack, state: RaceState): void {
  // roundWinnerId (set by updateElimination once the round decision is
  // made) is the authoritative "who is at the front" signal here, not the
  // cached leaderId: leaderId is set by updateCamera before updateElimination
  // runs that same tick, so it is stale if the leader itself was the one who
  // got eliminated on the very tick the round ended.
  const anchorId = state.roundWinnerId ?? state.leaderId;
  const leader = state.cars.find((c) => c.id === anchorId) ?? state.cars[0];
  if (!leader) return;
  const leaderProgress = leader.progress;

  const placeCar = (car: CarState, s: number, lateral: number): void => {
    const { pos: centrePos, tangent } = pointAtProgress(track, s);
    const dir = v.fromAngle(tangent);
    const right = { x: -dir.y, y: dir.x };
    car.pos = v.add(centrePos, v.scale(right, lateral));
    car.heading = tangent;
    car.vel = { x: 0, y: 0 };
    car.angVel = 0;
    car.airborne = 0;
    car.eliminated = false;
    car.wrongWayTime = 0;
    car.backDist = 0;

    const lap = Math.floor(s / track.length);
    const lapProgress = s - lap * track.length;
    car.lap = lap;
    car.lapProgress = lapProgress;
    car.progress = s;
    car.surface = surfaceAt(track, car.pos).name;

    state.offscreen[car.id] = 0;
  };

  placeCar(leader, leaderProgress, 0);

  // Reverse order of progress, per the spec: the car that was furthest
  // along lines up directly behind the leader and the tail-ender starts at
  // the back. Deliberately NOT rankCars' score-first order -- respawn
  // position is about where you were on track this round, not how many
  // rounds you have won.
  const followers = state.cars.filter((c) => c.id !== leader.id).sort((a, b) => b.progress - a.progress);

  followers.forEach((car, k) => {
    const lateral = k % 2 === 0 ? RESPAWN_LATERAL : -RESPAWN_LATERAL;
    const back = RESPAWN_STEP * (1 + Math.floor(k / 2));
    placeCar(car, leaderProgress - back, lateral);
  });

  // A small skin margin on top of the car radius avoids resolving to a
  // position that sits exactly on a wall edge, where floating-point error
  // can make collideWalls report a residual overlap of a few epsilons
  // forever and never quite reach null.
  const pushRadius = SPORTS_CAR.radius + 0.01;
  for (const car of state.cars) {
    for (let i = 0; i < 5; i++) {
      const push = collideWalls(track, car.pos, pushRadius);
      if (!push) break;
      resolveWallHit(car, push);
    }
  }
}

// Applies round-end scoring: the round winner gains a point, and every
// eliminated car loses a point, floored at 0. If every car was eliminated
// on the same tick, nobody loses a point (the winner still gains theirs).
export function applyRoundScores(state: RaceState, cfg: RaceConfig): void {
  const winnerId = state.roundWinnerId;
  if (winnerId === null) return;

  state.scores[winnerId] = (state.scores[winnerId] ?? 0) + 1;

  const allEliminated = state.cars.every((c) => c.eliminated);
  if (allEliminated) return;

  for (const car of state.cars) {
    if (car.id !== winnerId && car.eliminated) {
      state.scores[car.id] = Math.max(0, (state.scores[car.id] ?? 0) - 1);
    }
  }
}

// Ranks car ids by score descending, then lap descending, then lapProgress
// descending.
export function rankCars(state: RaceState): number[] {
  return [...state.cars]
    .sort((a, b) => {
      const scoreA = state.scores[a.id] ?? 0;
      const scoreB = state.scores[b.id] ?? 0;
      if (scoreB !== scoreA) return scoreB - scoreA;
      if (b.lap !== a.lap) return b.lap - a.lap;
      return b.lapProgress - a.lapProgress;
    })
    .map((c) => c.id);
}

// Finishes the race either when a car's lap reaches cfg.lapLimit (that car
// is awarded a point first, checked every racing tick) or when any score
// reaches cfg.pointCap (checked after applyRoundScores). A no-op once the
// race is already finished, so it never double-awards the lap-limit point.
export function checkFinished(state: RaceState, cfg: RaceConfig): void {
  if (state.phase === 'finished') return;

  for (const car of state.cars) {
    // An eliminated car cannot win the race on laps: it is out of the round
    // and coasting to a stop, so its lap count is frozen at whatever it had
    // when it fell off. Without this guard a car that reached the lap limit
    // and was eliminated on the very same tick would still take the race.
    if (car.eliminated) continue;
    if (car.lap >= cfg.lapLimit) {
      state.scores[car.id] = (state.scores[car.id] ?? 0) + 1;
      finishRace(state);
      return;
    }
  }

  for (const car of state.cars) {
    if ((state.scores[car.id] ?? 0) >= cfg.pointCap) {
      finishRace(state);
      return;
    }
  }
}

function finishRace(state: RaceState): void {
  state.phase = 'finished';
  state.phaseTime = 0;
  state.ranking = rankCars(state);
  state.winnerId = state.ranking[0] ?? null;
}

// Drives the race phase timing forward by one tick. Per-phase behaviour is
// documented in the module comment at the top of this file.
export function advancePhase(track: PreparedTrack, state: RaceState, cfg: RaceConfig, dt: number): void {
  state.tick += 1;

  switch (state.phase) {
    case 'countdown': {
      state.phaseTime += dt;
      if (state.phaseTime >= state.countdownLength) {
        state.phase = 'racing';
        state.phaseTime = 0;
      }
      break;
    }
    case 'racing': {
      for (const car of state.cars) updateProgress(track, car, dt);
      updateCamera(track, state, cfg, dt);
      updateElimination(track, state, cfg, dt);
      checkFinished(state, cfg);
      break;
    }
    case 'roundEnd': {
      state.phaseTime += dt;
      if (state.phaseTime >= cfg.roundEndPause) {
        applyRoundScores(state, cfg);
        respawnAll(track, state);
        checkFinished(state, cfg);
        // checkFinished may have mutated state.phase; widen the type back
        // from the switch case's narrowed 'roundEnd' literal to check it.
        if ((state.phase as RacePhase) !== 'finished') {
          state.phase = 'countdown';
          state.phaseTime = 0;
          state.countdownLength = 1.5;
          state.roundWinnerId = null;
        }
      }
      break;
    }
    case 'finished':
      break;
  }
}
